import {inject, Injectable} from '@angular/core';
import {CustomerWithVehicles} from '@service-and-repairs/calm-leads-customer-lib-api';
import {OrderSearchResult, VehicleSearchResult} from '@service-and-repairs/dms-api';
import {AwpDmsInterfaceResponseMapper} from '../mapping/awp-dms-interface-response-mapper.service';
import {SearchCustomersCustomerReference, SearchCustomersResponse} from '../models/customer';
import {SearchServiceOrdersFilter, SearchServiceOrdersResponse} from '../models/search-service-order';
import {SearchVehiclesFilter} from '../models/vehicle';
import {AwpDmsInterfaceService} from '../rest-api/awp-dms-interface.service';

@Injectable({providedIn: 'root'})
export class AwpDmsInterfaceGateway {
  public readonly awpService: AwpDmsInterfaceService = inject(AwpDmsInterfaceService);
  private readonly responseMapper: AwpDmsInterfaceResponseMapper = inject(AwpDmsInterfaceResponseMapper);

  searchVehiclesByLicensePlate(licensePlate: string,
                               resultSize: number,
                               locale: string,
                               dpNo: string,
                               outletNo: string): Promise<VehicleSearchResult> {
    return this.searchVehicles({numberPlate: licensePlate}, resultSize, locale, dpNo, outletNo);
  }

  async searchVehicles(filter: SearchVehiclesFilter,
                       resultSize: number,
                       locale: string,
                       dpNo: string,
                       outletNo: string): Promise<VehicleSearchResult> {
    const response = await this.awpService.searchVehicles(filter, locale, dpNo, outletNo, resultSize);
    return this.responseMapper.mapVehicleSearchResponse(response, resultSize);
  }

  async searchCustomersByNameViaAwpDmsInterface(firstName: string,
                                                lastName: string,
                                                locale: string,
                                                dpNo: string,
                                                outletNo: string): Promise<CustomerWithVehicles[]> {
    let customerList: SearchCustomersCustomerReference[] = [];

    try {
      const response: SearchCustomersResponse = await this.awpService.searchCustomers({
        customerData: {
          firstName,
          lastName
        }
      }, locale, dpNo, outletNo, 10, 10);
      customerList = response?.body?.customerList || [];
    } catch (error) {
      console.error('DmsService: Failed to search customers by name via AWP-DMS-Interface', error);
    }

    return customerList.map(ref =>
      this.responseMapper.mapSearchCustomersCustomerReferenceToCustomerWithVehicles(ref));
  }

  async searchCustomersByVin(vin17: string,
                             locale: string,
                             dpNo: string,
                             outletNo: string): Promise<CustomerWithVehicles[]> {
    let customerList: SearchCustomersCustomerReference[] = [];

    try {
      const response: SearchCustomersResponse = await this.awpService.searchCustomers(
        {vehicleData: {vin: vin17}}, locale, dpNo, outletNo, 10, 10);
      customerList = response?.body?.customerList || [];
    } catch (error) {
      console.error('DmsService: Failed to search customers by VIN via AWP-DMS-Interface', error);
    }

    return customerList.map(ref =>
      this.responseMapper.mapSearchCustomersCustomerReferenceToCustomerWithVehicles(ref));
  }

  async searchOrders(orderNumber: string | undefined,
                     vin: string | undefined,
                     resultSize: number,
                     locale: string,
                     dpNo: string,
                     outletNo: string): Promise<OrderSearchResult> {
    const filter: SearchServiceOrdersFilter = {};
    if (orderNumber) {
      filter.dmsServiceOrderId = orderNumber;
    }
    if (vin) {
      filter.vin = vin;
    }
    const response: SearchServiceOrdersResponse = await this.awpService.searchServiceOrders(
      filter,
      locale,
      dpNo,
      outletNo,
      resultSize
    );
    return this.responseMapper.mapOrderSearchResponse(response, resultSize);
  }
}
